<script>
  import { t } from '../i18nStore';
  import { localizeTaxRemarks } from '../services/taxCalculator';

  export let result;

  let copied = false;

  function formatCurrency(val) {
    if (val == null) return '0';
    return Number(val).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }

  function formatDateTime(dt) {
    const d = dt ? new Date(dt) : new Date();
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  function formatWarning(warn) {
    const matchSameBank = warn.match(/Each customer might enjoy only 1 tax rebate promotion on the same bank\/platform \((.+?)\)\./);
    if (matchSameBank) {
      return $t('tax.same_bank_warning', { bank: matchSameBank[1] });
    }
    const matchExpires = warn.match(/(.+?) expires before one of your scheduled installments\./);
    if (matchExpires) {
      return $t('tax.promo_expires_warning', { brand: matchExpires[1] });
    }
    return warn;
  }

  function copySummary() {
    const isCash = result.unit === '$';
    const rewardFormatted = `${isCash ? 'HK$' : ''}${formatCurrency(result.grandTotalReward)}${result.unit === 'miles' ? ' Miles' : ''}`;
    let text = `${$t('tax.share_card.summary_title')}\n`;
    text += `${$t('tax.share_card.calculated_on', { date: formatDateTime(result.calculationDate) })}\n`;
    text += `${$t('tax.share_card.total_tax_line', { tax: formatCurrency(result.totalTax) })}\n`;
    text += `${$t('tax.share_card.max_rebate_line', { rebate: rewardFormatted })}\n\n`;

    if (result.installmentResults && result.installmentResults.length > 1) {
      text += `${$t('tax.share_card.installment_header')}\n`;
      result.installmentResults.forEach(inst => {
        const dStr = inst.dueDate ? ` (${$t('tax.share_card.due_date', { date: new Date(inst.dueDate).toISOString().slice(0, 10) })})` : '';
        const subtotalReward = `${isCash ? 'HK$' : ''}${formatCurrency(inst.subtotalReward)}${result.unit === 'miles' ? ' Miles' : ''}`;
        text += `${$t('tax.share_card.installment_row', {
          index: inst.installmentIndex,
          amount: formatCurrency(inst.installmentAmount),
          due: dStr,
          rebate: subtotalReward
        })}\n`;
        inst.allocations.forEach(alloc => {
          const rText = alloc.remarks ? ` (${localizeTaxRemarks(alloc.remarks, $t)})` : '';
          text += `   - ${alloc.brand}: HK$${formatCurrency(alloc.amountSpent)} -> +${isCash ? 'HK$' : ''}${formatCurrency(alloc.rewardEarned)}${result.unit === 'miles' ? ' Miles' : ''}${rText}\n`;
        });
      });
    } else if (result.installmentResults && result.installmentResults.length === 1) {
      text += `${$t('tax.share_card.strategy_header')}\n`;
      result.installmentResults[0].allocations.forEach(alloc => {
        const rText = alloc.remarks ? ` (${localizeTaxRemarks(alloc.remarks, $t)})` : '';
        text += `• ${alloc.brand}: HK$${formatCurrency(alloc.amountSpent)} -> +${isCash ? 'HK$' : ''}${formatCurrency(alloc.rewardEarned)}${result.unit === 'miles' ? ' Miles' : ''}${rText}\n`;
      });
    }

    text += `\n${$t('tax.share_card.cta')}`;

    navigator.clipboard.writeText(text).then(() => {
      copied = true;
      setTimeout(() => (copied = false), 3000);
    });
  }
</script>

<div class="share-card-container">
  <!-- Brand Ribbon Header -->
  <div class="card-header">
    <div class="brand-left">
      <div class="brand-badge">⚡</div>
      <div class="brand-titles">
        <span class="brand-name">SNAP PERK</span>
        <span class="brand-tagline">{$t('tax.share_card.tagline')}</span>
      </div>
    </div>
    <div class="brand-right">
      <span class="website-link">www.snapperk.app</span>
      <span class="calc-date">📅 {formatDateTime(result.calculationDate)}</span>
    </div>
  </div>

  <!-- Main Highlight Container -->
  <div class="card-body">
    <div class="hero-overview">
      <div class="overview-box">
        <span class="label">{$t('tax.share_card.total_tax')}</span>
        <span class="tax-value">HK$&nbsp;{formatCurrency(result.totalTax)}</span>
      </div>
      <div class="overview-box highlight">
        <span class="label green">{result.crossPromotion ? $t('tax.share_card.optimized_rebate') : $t('tax.share_card.best_rebate')}</span>
        <span class="rebate-value">
          {result.unit === '$' ? '+HK$\u00A0' : '+'}{formatCurrency(result.grandTotalReward)}{result.unit === 'miles' ? '\u00A0Miles' : ''}
        </span>
      </div>
    </div>

    <!-- Strategy Breakdown -->
    <div class="breakdown-section">
      <h4 class="section-title">
        {result.installmentResults && result.installmentResults.length > 1 ? $t('tax.share_card.installment_schedule') : $t('tax.share_card.allocation_strategy')}
      </h4>

      {#if result.installmentResults}
        {#each result.installmentResults as inst}
          <div class="installment-block">
            <div class="inst-header">
              <span class="inst-num">{$t('tax.share_card.installment_item', { index: inst.installmentIndex, amount: formatCurrency(inst.installmentAmount) })}</span>
              <span class="inst-subtotal">
                {result.unit === '$' ? '+HK$\u00A0' : '+'}{formatCurrency(inst.subtotalReward)}{result.unit === 'miles' ? '\u00A0Miles' : ''}
              </span>
            </div>
            {#if inst.dueDate}
              <div class="inst-due-date">{$t('tax.share_card.due_date', { date: new Date(inst.dueDate).toISOString().slice(0, 10) })}</div>
            {/if}

            <div class="allocations-list">
              {#each inst.allocations as alloc}
                <div class="alloc-row">
                  <span class="alloc-brand">{alloc.brand}</span>
                  <span class="alloc-spend">HK$&nbsp;{formatCurrency(alloc.amountSpent)}</span>
                  <span class="alloc-reward">+{result.unit === '$' ? 'HK$\u00A0' : ''}{formatCurrency(alloc.rewardEarned)}{result.unit === 'miles' ? '\u00A0Miles' : ''}</span>
                  {#if alloc.remarks}
                    <span class="alloc-remarks">{localizeTaxRemarks(alloc.remarks, $t)}</span>
                  {/if}
                </div>
              {/each}
            </div>
          </div>
        {/each}
      {/if}
    </div>

    {#if result.warnings && result.warnings.length > 0}
      <div class="warnings-box">
        {#each result.warnings as warn}
          <div class="warn-line">⚠️ {formatWarning(warn)}</div>
        {/each}
      </div>
    {/if}

    <!-- Footer Watermark & Action Button -->
    <div class="card-footer">
      <span class="watermark-text">{$t('tax.share_card.watermark')}</span>
      <button class="share-btn" on:click={copySummary}>
        {$t(copied ? 'tax.share_card.copied' : 'tax.share_card.share')}
      </button>
    </div>
  </div>
</div>

<style>
  .share-card-container {
    background: #ffffff;
    border-radius: 16px;
    border: 2px solid #002366;
    box-shadow: 0 10px 25px rgba(0, 35, 102, 0.12);
    overflow: hidden;
    margin-top: 1.5rem;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  }

  .card-header {
    background: #002366;
    color: white;
    padding: 12px 18px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
  }

  .brand-left {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .brand-badge {
    background: #d4af37;
    color: #002366;
    font-size: 14px;
    font-weight: bold;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
  }

  .brand-titles {
    display: flex;
    flex-direction: column;
  }

  .brand-name {
    font-weight: 800;
    font-size: 15px;
    letter-spacing: 1px;
    color: #ffffff;
  }

  .brand-tagline {
    font-size: 10px;
    color: #d4af37;
    font-weight: 600;
  }

  .brand-right {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }

  .website-link {
    font-size: 12px;
    color: #ffffff;
    opacity: 0.85;
    font-weight: 600;
  }

  .calc-date {
    font-size: 11px;
    color: #d4af37;
    font-weight: 700;
  }

  .card-body {
    padding: 20px;
  }

  .hero-overview {
    display: flex;
    justify-content: space-between;
    background: linear-gradient(135deg, rgba(27, 94, 32, 0.08), rgba(212, 175, 55, 0.12));
    border: 1px solid rgba(46, 125, 50, 0.3);
    border-radius: 12px;
    padding: 14px 18px;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 10px;
  }

  .overview-box {
    display: flex;
    flex-direction: column;
  }

  .overview-box.highlight {
    align-items: flex-end;
  }

  .label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.5px;
    color: #555;
  }

  .label.green {
    color: #1b5e20;
  }

  .tax-value {
    font-size: 20px;
    font-weight: 800;
    color: #222;
    white-space: nowrap;
  }

  .rebate-value {
    font-size: 24px;
    font-weight: 900;
    color: #1b5e20;
    white-space: nowrap;
  }

  .section-title {
    font-size: 13px;
    font-weight: 800;
    letter-spacing: 0.6px;
    color: #002366;
    margin: 0 0 10px 0;
  }

  .installment-block {
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 10px;
    padding: 12px 14px;
    margin-bottom: 12px;
  }

  .inst-header {
    display: flex;
    justify-content: space-between;
    font-weight: 700;
    font-size: 14px;
    color: #333;
    gap: 8px;
  }

  .inst-num {
    white-space: nowrap;
  }

  .inst-subtotal {
    color: #1b5e20;
    font-weight: 800;
    white-space: nowrap;
  }

  .inst-due-date {
    font-size: 11px;
    color: #777;
    margin-top: 2px;
    margin-bottom: 8px;
  }

  .allocations-list {
    border-top: 1px dashed #dee2e6;
    padding-top: 8px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .alloc-row {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    flex-wrap: wrap;
  }

  .alloc-brand {
    background: rgba(0, 35, 102, 0.1);
    color: #002366;
    font-weight: 700;
    font-size: 11px;
    padding: 2px 8px;
    border-radius: 4px;
  }

  .alloc-spend {
    color: #333;
    font-weight: 500;
    white-space: nowrap;
  }

  .alloc-reward {
    color: #28a745;
    font-weight: 700;
    white-space: nowrap;
  }

  .alloc-remarks {
    font-size: 11px;
    color: #6c757d;
    font-style: italic;
  }

  .warnings-box {
    background: #fff3cd;
    border: 1px solid #ffeeba;
    color: #856404;
    padding: 10px;
    border-radius: 8px;
    font-size: 12px;
    margin-top: 10px;
  }

  .card-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px solid #e9ecef;
    flex-wrap: wrap;
    gap: 10px;
  }

  .watermark-text {
    font-size: 11px;
    color: #6c757d;
    font-weight: 500;
  }

  .share-btn {
    background: #002366;
    color: white;
    border: none;
    padding: 6px 14px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.2s;
  }

  .share-btn:hover {
    background: #001844;
  }

  @media (max-width: 640px) {
    .share-card-container {
      border-radius: 10px;
      margin-top: 0.75rem;
    }

    .card-header {
      padding: 10px 12px;
      gap: 6px;
    }

    .card-body {
      padding: 12px 10px;
    }

    .hero-overview {
      padding: 10px 12px;
      margin-bottom: 12px;
      gap: 8px;
    }

    .tax-value {
      font-size: 16px;
    }

    .rebate-value {
      font-size: 18px;
    }

    .installment-block {
      padding: 10px 8px;
      margin-bottom: 8px;
      border-radius: 8px;
    }

    .inst-header {
      font-size: 13px;
    }

    .alloc-row {
      gap: 6px;
      font-size: 12px;
    }

    .alloc-remarks {
      width: 100%;
    }

    .card-footer {
      margin-top: 10px;
      padding-top: 10px;
    }
  }
</style>
